import { useEffect, useState } from 'react';
import { QUALITY_CHANGED_EVENT, getGate } from '../services/QualityService';

/**
 * The project's release gate for the whole page (`GET /quality/gate`): `{ gate, running, loading, error }`. One answer
 * per project, shared by every component that asks (a module-level cache), read again every 60 s while the page is
 * visible and at once on `flow:quality-changed` (the Quality tab and the Publish dialog send it after a run, an
 * override or a settings save). An older sandbox (404 `sandbox_outdated`) or no vfs-service: `gate` null, `error` set,
 * nothing shown anywhere (callers treat it as "not blocked").
 */
export const GATE_REFRESH_MS = 60000;
const EMPTY = Object.freeze({ gate: null, running: null, loading: false, error: null });
const entries = new Map();

function entryOf(projectId) {
  if (!entries.has(projectId)) {
    entries.set(projectId, { value: { ...EMPTY, loading: true }, at: 0, inflight: null, listeners: new Set() });
  }
  return entries.get(projectId);
}

function publish(entry, value) {
  entry.value = value;
  entry.listeners.forEach((listener) => listener(value));
}

/** Reads the gate again (one call at a time per project); resolves to the new value. */
export function refreshQualityGate(projectId) {
  if (!projectId) { return Promise.resolve(EMPTY); }
  const entry = entryOf(projectId);
  if (entry.inflight) { return entry.inflight; }
  entry.inflight = getGate(projectId)
    .then((answer) => ({ gate: answer?.gate || null, running: answer?.running || null, loading: false, error: null }))
    .catch((error) => ({ gate: null, running: null, loading: false, error }))
    .then((value) => {
      entry.at = Date.now();
      entry.inflight = null;
      publish(entry, value);
      return value;
    });
  return entry.inflight;
}

/** For tests: forget every project's answer. */
export function resetQualityGateCache() {
  entries.clear();
}

/** How many gate rules block Publish now (0 when it passes, is overridden or unknown): the tabs' `qualityBlocked`. */
export const blockedCount = (gate) => (gate?.blocked ? gate.failing || 0 : 0);

export default function useQualityGate(projectId) {
  const [value, setValue] = useState(() => (projectId ? entryOf(projectId).value : EMPTY));

  useEffect(() => {
    if (!projectId) { setValue(EMPTY); return undefined; }
    const entry = entryOf(projectId);
    setValue(entry.value);
    entry.listeners.add(setValue);
    const stale = () => Date.now() - entry.at >= GATE_REFRESH_MS;
    if (stale()) { refreshQualityGate(projectId); }
    const visible = () => typeof document === 'undefined' || !document.hidden;
    const timer = setInterval(() => { if (visible()) { refreshQualityGate(projectId); } }, GATE_REFRESH_MS);
    const onChanged = (event) => {
      const target = event?.detail?.projectId;
      if (!target || target === projectId) { refreshQualityGate(projectId); }
    };
    const onVisible = () => { if (visible() && stale()) { refreshQualityGate(projectId); } };
    window.addEventListener(QUALITY_CHANGED_EVENT, onChanged);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      entry.listeners.delete(setValue);
      clearInterval(timer);
      window.removeEventListener(QUALITY_CHANGED_EVENT, onChanged);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [projectId]);

  return value;
}
